import type { ThemeOptions } from '@mui/material/styles';

const FONT = '"Plus Jakarta Sans", "Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif';

export const brand = {
  sidebarGradient: 'linear-gradient(180deg, #0B2239 0%, #091D30 100%)',
  sidebarText: '#B8C7D6',
  sidebarTextActive: '#FFFFFF',
  activePill: '#0E7490',
  promoGradient: 'linear-gradient(145deg, rgba(14,116,144,.22) 0%, rgba(18,62,89,.42) 100%)',
  heroGradient: 'linear-gradient(112deg, #123E59 0%, #0E6173 100%)',
  avatarGradient: 'linear-gradient(135deg, #16879B 0%, #0E6173 100%)',
  pageGlow: 'radial-gradient(circle at 58% -10%, rgba(14,116,144,.08), transparent 37%), radial-gradient(circle at 100% 0%, rgba(37,99,235,.045), transparent 30%)',
};

const palette = {
  primary: { main: '#0E7490', light: '#1696A7', dark: '#0B5263', contrastText: '#ffffff' },
  secondary: { main: '#1D4E6D', contrastText: '#ffffff' },
  success: { main: '#0FAF78', light: '#37C998', dark: '#07845A', contrastText: '#ffffff' },
  error: { main: '#E94B5F' },
  warning: { main: '#F59E0B', contrastText: '#111827' },
  info: { main: '#2F80ED' },
};

const typography: ThemeOptions['typography'] = {
  fontFamily: FONT,
  h1: { fontWeight: 850, letterSpacing: '-0.035em' },
  h2: { fontWeight: 850, letterSpacing: '-0.03em' },
  h3: { fontWeight: 850, letterSpacing: '-0.025em' },
  h4: { fontWeight: 850, letterSpacing: '-0.022em' },
  h5: { fontWeight: 800, letterSpacing: '-0.018em' },
  h6: { fontWeight: 780, letterSpacing: '-0.01em' },
  subtitle1: { fontWeight: 750 },
  subtitle2: { fontWeight: 700 },
  overline: { fontWeight: 800, letterSpacing: '0.065em' },
  button: { textTransform: 'none', fontWeight: 750, letterSpacing: '-0.005em' },
  body1: { lineHeight: 1.55 },
  body2: { lineHeight: 1.55 },
};

const components = (dark: boolean): ThemeOptions['components'] => ({
  MuiCssBaseline: {
    styleOverrides: {
      html: { scrollBehavior: 'smooth', maxWidth: '100%', overflowX: 'clip' },
      body: { margin: 0, minWidth: 320, maxWidth: '100%', overflowX: 'clip' },
      '#root': { width: '100%', maxWidth: '100%', minHeight: '100vh', overflowX: 'clip' },
      'th, td': { fontVariantNumeric: 'tabular-nums' },
      '::selection': { backgroundColor: 'rgba(14,116,144,.18)' },
      '*': { boxSizing: 'border-box' },
      '*::-webkit-scrollbar': { width: 8, height: 8 },
      '*::-webkit-scrollbar-track': { background: 'transparent' },
      '*::-webkit-scrollbar-thumb': { background: 'rgba(14,116,144,.24)', borderRadius: 12 },
      'input[type=number]': { fontVariantNumeric: 'tabular-nums' },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: 12,
        boxShadow: 'none',
        minHeight: 40,
        paddingInline: 17,
        transition: 'transform .18s ease, box-shadow .18s ease, background-color .18s ease',
        '&:active': { transform: 'translateY(1px)' },
        '&.MuiButton-containedPrimary': {
          background: '#0E7490',
          boxShadow: '0 10px 24px -14px rgba(14,82,99,.72)',
          '&:hover': { background: '#0B667D', boxShadow: '0 14px 28px -16px rgba(14,82,99,.78)' },
        },
      },
      outlined: { borderColor: dark ? 'rgba(255,255,255,.18)' : 'rgba(14,116,144,.24)' },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: { borderRadius: 12 },
    },
  },
  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: '1px solid',
        borderColor: dark ? 'rgba(255,255,255,.08)' : 'rgba(15,42,61,.10)',
        backgroundImage: 'none',
        boxShadow: dark ? 'none' : '0 18px 46px -38px rgba(15,42,61,.55)',
      },
    },
  },
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      rounded: { borderRadius: 12 },
      root: { backgroundImage: 'none' },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 999, fontWeight: 700 },
      sizeSmall: { height: 28 },
    },
  },
  MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        minHeight: 44,
        borderRadius: 12,
        backgroundColor: dark ? 'rgba(255,255,255,.02)' : '#fff',
        transition: 'box-shadow .18s ease, background-color .18s ease',
        '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(14,116,144,.10)' },
      },
      notchedOutline: { borderColor: dark ? 'rgba(255,255,255,.12)' : 'rgba(47,39,86,.14)' },
    },
  },
  MuiInputLabel: { styleOverrides: { root: { fontWeight: 600 } } },
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontVariantNumeric: 'tabular-nums',
        borderColor: dark ? 'rgba(255,255,255,.06)' : 'rgba(15,23,42,.06)',
      },
      head: { fontWeight: 800, whiteSpace: 'nowrap', color: dark ? '#AFC1CD' : '#405466' },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: { '&:hover': { backgroundColor: dark ? 'rgba(255,255,255,.04)' : 'rgba(14,116,144,.04)' } },
    },
  },
  MuiAppBar: {
    defaultProps: { elevation: 0 },
    styleOverrides: { root: { backgroundImage: 'none' } },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 12, alignItems: 'center' },
    },
  },
  MuiDialog: {
    styleOverrides: { paper: { borderRadius: 12 } },
  },
  MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 12, fontSize: 12 } } },
});

export const lightTheme: ThemeOptions = {
  palette: {
    mode: 'light',
    ...palette,
    background: { default: '#F5F7F9', paper: '#FFFFFF' },
    text: { primary: '#152536', secondary: '#607080' },
    divider: 'rgba(15,42,61,.09)',
    action: { hover: 'rgba(14,116,144,.055)', selected: 'rgba(14,116,144,.095)' },
  },
  shape: { borderRadius: 12 },
  spacing: 8,
  typography,
  components: components(false),
};

export const darkTheme: ThemeOptions = {
  palette: {
    mode: 'dark',
    ...palette,
    primary: { main: '#39AFC0', light: '#66C3D0', dark: '#16879B', contrastText: '#071A26' },
    background: { default: '#071A26', paper: '#0D2635' },
    text: { primary: '#EDF5F7', secondary: '#A5B8C2' },
    divider: 'rgba(255,255,255,.10)',
  },
  shape: { borderRadius: 12 },
  spacing: 8,
  typography,
  components: components(true),
};
